/**
 * Redux Actions for Orders and E-Learning Invitations
 *
 * Handles fetching orders, creating and updating invitations
 * Uses operation status pattern from @uncinc/react-drupal-core
 *
 * Note: The /api/orders endpoint returns a SLIM response format
 * designed for the orders list view. Detail pages will need
 * a separate endpoint or fetch the full order data.
 */

import {
  setOperationStatus,
  UI_OPERATION_STATUS_REQUEST,
  UI_OPERATION_STATUS_SUCCESS,
  UI_OPERATION_STATUS_ERROR,
} from '@uncinc/react-drupal-core';
import config from 'react-global-configuration';
import type {
  Order,
  OrdersApiResponse,
  OrderDetailResponse,
  ElearningInvitationsResponse,
  CreateInvitationRequest,
  UpdateInvitationRequest,
  InvitationApiResponse,
  InvitationErrorCode,
} from '../../../types/orders';

/**
 * Custom error class for invitation API errors with error code support
 */
export class InvitationError extends Error {
  errorCode: InvitationErrorCode;

  constructor(message: string, errorCode: InvitationErrorCode = 'UNKNOWN_ERROR') {
    super(message);
    this.name = 'InvitationError';
    this.errorCode = errorCode;
  }
}

// Action types
export const SET_ORDERS_DATA = 'SET_ORDERS_DATA';
export const APPEND_ORDERS_DATA = 'APPEND_ORDERS_DATA';
export const SET_ORDERS_PAGINATION = 'SET_ORDERS_PAGINATION';

// Helper function to get API URL
const getApiUrl = (): string => {
  const baseUrl = config.get('API_URL') || '/api';
  if (baseUrl.startsWith('//')) {
    return baseUrl.replace('//', 'http://');
  }
  return baseUrl;
};

// Helper function to get CSRF token
// NOTE: The /session/token endpoint returns plain text, not JSON
const getCsrfToken = async (): Promise<string> => {
  try {
    const response = await fetch('/session/token', {
      method: 'GET',
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Failed to fetch CSRF token');
    }
    // Response is plain text token, not JSON
    const token = await response.text();
    return token.trim();
  } catch (error) {
    console.error('Error fetching CSRF token:', error);
    throw error;
  }
};

/**
 * Fetch orders for the current user with pagination
 * GET /api/orders?_format=json&limit=X&offset=Y
 *
 * @param limit - Number of orders to fetch (default: 5)
 * @param offset - Number of orders to skip (default: 0)
 * @param append - Whether to append to existing orders (for load more) or replace
 */
export const fetchOrders = (
  limit: number = 5,
  offset: number = 0,
  append: boolean = false,
) => async (dispatch: any) => {
  dispatch(setOperationStatus('orders', 'fetch', UI_OPERATION_STATUS_REQUEST, null));

  try {
    const url = `${getApiUrl()}/api/orders?_format=json&limit=${limit}&offset=${offset}`;
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 403) {
        // Not authenticated
        throw new Error('UNAUTHENTICATED');
      }
      throw new Error(`HTTP ${response.status}`);
    }

    const data: OrdersApiResponse = await response.json();

    // Dispatch orders - append or replace based on parameter
    if (append) {
      dispatch({
        type: APPEND_ORDERS_DATA,
        payload: data.orders,
      });
    } else {
      dispatch({
        type: SET_ORDERS_DATA,
        payload: data.orders,
      });
    }

    // Update pagination state
    dispatch({
      type: SET_ORDERS_PAGINATION,
      payload: {
        total: data.total,
        limit: data.limit,
        offset: data.offset,
        hasMore: data.has_more,
      },
    });

    dispatch(setOperationStatus('orders', 'fetch', UI_OPERATION_STATUS_SUCCESS, null));
  } catch (error) {
    console.error('Error fetching orders:', error);
    dispatch(setOperationStatus('orders', 'fetch', UI_OPERATION_STATUS_ERROR, null));
    throw error;
  }
};

/**
 * Create a new invitation for an order item
 * POST /api/elearning-invitations
 */
// eslint-disable-next-line max-len
export const createInvitation = (
  orderId: number,
  orderItemId: number,
  email: string,
) => async (dispatch: any) => {
  dispatch(setOperationStatus('orders', 'create_invitation', UI_OPERATION_STATUS_REQUEST, null));

  try {
    const csrfToken = await getCsrfToken();

    const requestBody: CreateInvitationRequest = {
      order_id: orderId,
      order_item_id: orderItemId,
      email,
    };

    const response = await fetch(`${getApiUrl()}/api/elearning-invitations?_format=json`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify(requestBody),
    });

    // Parse response - both success and error responses return JSON
    let data;
    try {
      data = await response.json();
    } catch {
      // JSON parsing failed - create generic error
      if (!response.ok) {
        throw new InvitationError(`HTTP ${response.status}`, 'UNKNOWN_ERROR');
      }
      throw new Error('Invalid response format');
    }

    if (!response.ok) {
      // Extract error_code from structured error response
      throw new InvitationError(
        data.message || `HTTP ${response.status}`,
        data.error_code || 'UNKNOWN_ERROR',
      );
    }

    const newInvitation: InvitationApiResponse = data;

    // Refresh orders to get updated invitation list
    dispatch(fetchOrders());
    dispatch(setOperationStatus('orders', 'create_invitation', UI_OPERATION_STATUS_SUCCESS, null));

    return newInvitation;
  } catch (error) {
    console.error('Error creating invitation:', error);
    dispatch(setOperationStatus('orders', 'create_invitation', UI_OPERATION_STATUS_ERROR, null));
    throw error;
  }
};

/**
 * Update an existing invitation (change email address)
 * PATCH /api/elearning-invitations/{invitation_id}
 * Only allowed when invitation status is 'invited'
 */
export const updateInvitation = (invitationId: number, email: string) => async (
  dispatch: any,
) => {
  dispatch(setOperationStatus('orders', 'update_invitation', UI_OPERATION_STATUS_REQUEST, null));

  try {
    const csrfToken = await getCsrfToken();

    const requestBody: UpdateInvitationRequest = { email };

    const response = await fetch(`${getApiUrl()}/api/elearning-invitations/${invitationId}?_format=json`, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify(requestBody),
    });

    // Parse response - both success and error responses return JSON
    let data;
    try {
      data = await response.json();
    } catch {
      // JSON parsing failed - create generic error
      if (!response.ok) {
        throw new InvitationError(`HTTP ${response.status}`, 'UNKNOWN_ERROR');
      }
      throw new Error('Invalid response format');
    }

    if (!response.ok) {
      // Extract error_code from structured error response
      throw new InvitationError(
        data.message || `HTTP ${response.status}`,
        data.error_code || 'UNKNOWN_ERROR',
      );
    }

    const updatedInvitation: InvitationApiResponse = data;

    // Refresh orders to get updated invitation
    dispatch(fetchOrders());
    dispatch(setOperationStatus('orders', 'update_invitation', UI_OPERATION_STATUS_SUCCESS, null));

    return updatedInvitation;
  } catch (error) {
    console.error('Error updating invitation:', error);
    dispatch(setOperationStatus('orders', 'update_invitation', UI_OPERATION_STATUS_ERROR, null));
    throw error;
  }
};

/**
 * Delete an invitation
 * DELETE /api/elearning-invitations/{invitation_id}
 *
 * TODO: Awaiting backend endpoint implementation
 */
export const deleteInvitation = (invitationId: number) => async (dispatch: any) => {
  dispatch(setOperationStatus('orders', 'delete_invitation', UI_OPERATION_STATUS_REQUEST, null));

  try {
    const csrfToken = await getCsrfToken();

    const response = await fetch(`${getApiUrl()}/api/elearning-invitations/${invitationId}?_format=json`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': csrfToken,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}`);
    }

    // Refresh orders to get updated invitation list
    dispatch(fetchOrders());
    dispatch(setOperationStatus('orders', 'delete_invitation', UI_OPERATION_STATUS_SUCCESS, null));
  } catch (error) {
    console.error('Error deleting invitation:', error);
    dispatch(setOperationStatus('orders', 'delete_invitation', UI_OPERATION_STATUS_ERROR, null));
    throw error;
  }
};

/**
 * Set orders data in Redux state
 */
export const setOrdersData = (orders: Order[]) => ({
  type: SET_ORDERS_DATA,
  payload: orders,
});

/**
 * Fetch a single order detail by ID
 * GET /api/orders/{orderId}?_format=json
 *
 * @param orderId - The order ID to fetch
 * @returns The order detail response
 */
export const fetchOrderDetail = (
  orderId: string | number,
) => async (dispatch: any): Promise<OrderDetailResponse> => {
  dispatch(setOperationStatus('orders', 'fetch_detail', UI_OPERATION_STATUS_REQUEST, null));

  try {
    const url = `${getApiUrl()}/api/orders/${orderId}?_format=json`;
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('UNAUTHENTICATED');
      }
      if (response.status === 404) {
        throw new Error('ORDER_NOT_FOUND');
      }
      throw new Error(`HTTP ${response.status}`);
    }

    const data: OrderDetailResponse = await response.json();

    dispatch(setOperationStatus('orders', 'fetch_detail', UI_OPERATION_STATUS_SUCCESS, null));
    return data;
  } catch (error) {
    console.error('Error fetching order detail:', error);
    dispatch(setOperationStatus('orders', 'fetch_detail', UI_OPERATION_STATUS_ERROR, null));
    throw error;
  }
};

/**
 * Response from the activation endpoint
 */
export interface ActivationResponse {
  success?: boolean;
  course_url?: string;
  error?: boolean;
  error_code?: string;
  message?: string;
}

/**
 * Activates an e-learning invitation (requires authentication).
 *
 * This is a standalone async function (not a Redux thunk) because:
 * - No Redux state needs updating (it's a one-time activation)
 * - Simpler than full Redux flow for a fire-and-forget action
 *
 * The endpoint requires a Drupal session (credentials: 'include') and
 * verifies that the logged-in user's email matches the invitation.
 *
 * POST /api/elearning/activate
 */
export const activateElearningInvitation = async (
  invitationId: number,
  token: string,
): Promise<ActivationResponse> => {
  const baseUrl = getApiUrl();
  const response = await fetch(`${baseUrl}/api/elearning/activate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ invitation_id: invitationId, token }),
  });

  return response.json() as Promise<ActivationResponse>;
};

/**
 * Fetch e-learning invitations for a specific order
 * GET /api/elearning-invitations?_format=json&order_id={orderId}
 *
 * @param orderId - The order ID to fetch invitations for
 * @returns The invitations response
 */
export const fetchOrderInvitations = (
  orderId: string | number,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
) => async (dispatch: any): Promise<ElearningInvitationsResponse> => {
  dispatch(
    setOperationStatus('orders', 'fetch_invitations', UI_OPERATION_STATUS_REQUEST, null),
  );

  try {
    const url = `${getApiUrl()}/api/elearning-invitations?_format=json&order_id=${orderId}`;
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('UNAUTHENTICATED');
      }
      if (response.status === 404) {
        throw new Error('ORDER_NOT_FOUND');
      }
      throw new Error(`HTTP ${response.status}`);
    }

    const data: ElearningInvitationsResponse = await response.json();

    dispatch(
      setOperationStatus('orders', 'fetch_invitations', UI_OPERATION_STATUS_SUCCESS, null),
    );
    return data;
  } catch (error) {
    console.error('Error fetching order invitations:', error);
    dispatch(
      setOperationStatus('orders', 'fetch_invitations', UI_OPERATION_STATUS_ERROR, null),
    );
    throw error;
  }
};
